import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-product-list',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ConfirmationModalComponent],
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.css']
})
export class ProductListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLDivElement>;
  
  products: any[] = [];
  categories: any[] = [];
  loading = false;
  
  // Pagination (Infinite Scroll)
  currentPage = 1;
  itemsPerPage = 5; // Show 5 items per load
  totalPages = 1;
  totalItems = 0;
  isLoadingMore = false; // Track if loading more data
  hasMoreData = true; // Track if there's more data to load
  
  // Search and filters
  searchTerm = '';
  selectedCategory = '';
  selectedStatus = '';
  showActionMenu: { [key: string]: boolean } = {};
  errorMessage = ''; // Error notification message

  // Confirmation modal state
  showConfirmModal = false;
  productToToggle: any = null;
  modalMessage = '';
  modalConfirmText = 'Confirm';

  private scrollHandler: any = null;
  private scrollableParent: HTMLElement | null = null;

  constructor(
    private apiService: ApiService,
    private router: Router,
    public authService: AuthService,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.loadProducts();
    this.loadCategories();
    // Wait for view to initialize, then attach scroll listener
    setTimeout(() => {
      this.attachScrollListener();
    }, 100);
  }

  ngAfterViewInit() {
    // Attach scroll listener after view is initialized
    this.attachScrollListener();
  }

  ngOnDestroy() {
    if (this.scrollHandler) {
      if (this.scrollableParent) {
        this.scrollableParent.removeEventListener('scroll', this.scrollHandler);
      }
      if (this.tableContainer?.nativeElement) {
        this.tableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
      }
    }
  }

  /** Find the scrollable ancestor (e.g. main-content) so we can listen to page scroll */
  private findScrollableParent(): HTMLElement | null {
    let el: HTMLElement | null = this.elementRef.nativeElement;
    while (el && el !== document.body) {
      el = el.parentElement;
      if (!el) break;
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return el;
      }
    }
    return null;
  }

  attachScrollListener() {
    if (!this.scrollHandler) {
      this.scrollHandler = this.throttle(() => this.onScroll(), 200);
    }

    // Listen to the scrollable page container (main-content) so scrolling the page triggers load more
    if (!this.scrollableParent) {
      this.scrollableParent = this.findScrollableParent();
    }
    if (this.scrollableParent) {
      this.scrollableParent.removeEventListener('scroll', this.scrollHandler);
      this.scrollableParent.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    // Also listen to table container scroll (inner 450px box) when it exists
    if (this.tableContainer?.nativeElement) {
      this.tableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
      this.tableContainer.nativeElement.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  // Throttle function to limit scroll event calls
  private throttle(func: Function, limit: number) {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, ...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  onScroll() {
    const threshold = 150;

    const isNearBottom = (el: HTMLElement): boolean => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      return scrollHeight - (scrollTop + clientHeight) <= threshold;
    };

    let nearBottom = false;
    if (this.scrollableParent && this.scrollableParent.scrollHeight > this.scrollableParent.clientHeight) {
      nearBottom = isNearBottom(this.scrollableParent);
    }
    if (!nearBottom && this.tableContainer?.nativeElement) {
      const tc = this.tableContainer.nativeElement;
      if (tc.scrollHeight > tc.clientHeight) {
        nearBottom = isNearBottom(tc);
      }
    }

    if (nearBottom && this.hasMoreData && !this.isLoadingMore && !this.loading) {
      this.loadMoreProducts();
    }
  }

  loadProducts() {
    this.loading = true;
    this.errorMessage = '';
    this.currentPage = 1; // Reset to first page
    this.hasMoreData = true; // Reset has more data flag
    
    const status = this.selectedStatus || undefined;
    const category = this.selectedCategory || undefined;
    const search = this.searchTerm || undefined;
    
    // Fetch products with pagination
    this.apiService.getProducts(this.currentPage, this.itemsPerPage, search, status, category).subscribe({
      next: (response: any) => {
        // Interceptor may wrap as { data }; use unwrapped payload
        const data = (response && response.data !== undefined) ? response.data : response;
        if (data && data.products && data.pagination) {
          this.products = data.products.map((product: any) => {
            product.sharedCount = product.sharedSuppliers?.length || 0;
            return product;
          });
          this.currentPage = data.pagination.currentPage;
          this.totalPages = data.pagination.totalPages;
          this.totalItems = data.pagination.totalItems;
          this.hasMoreData = this.currentPage < this.totalPages;
          setTimeout(() => this.attachScrollListener(), 50);
        } else {
          this.products = Array.isArray(data) ? data : [];
          this.hasMoreData = false;
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading products:', err);
        this.errorMessage = err.error?.message || 'Error loading products';
        this.loading = false;
        setTimeout(() => {
          this.errorMessage = '';
        }, 5000);
      }
    });
  }

  loadMoreProducts() {
    // Don't load if already loading or no more data
    if (this.isLoadingMore || !this.hasMoreData || this.loading) {
      return;
    }

    // Check if there are more pages
    if (this.currentPage < this.totalPages) {
      this.isLoadingMore = true;
      const nextPage = this.currentPage + 1;
      this.currentPage = nextPage;
      
      const status = this.selectedStatus || undefined;
      const category = this.selectedCategory || undefined;
      const search = this.searchTerm || undefined;
      
      this.apiService.getProducts(nextPage, this.itemsPerPage, search, status, category).subscribe({
        next: (response: any) => {
          const data = (response && response.data !== undefined) ? response.data : response;
          if (data && data.products && data.pagination) {
            const newProducts = data.products.map((product: any) => {
              product.sharedCount = product.sharedSuppliers?.length || 0;
              return product;
            });
            this.products = [...this.products, ...newProducts];
            this.currentPage = data.pagination.currentPage;
            this.totalPages = data.pagination.totalPages;
            this.totalItems = data.pagination.totalItems;
            this.hasMoreData = this.currentPage < this.totalPages;
            setTimeout(() => this.attachScrollListener(), 50);
          }
          this.isLoadingMore = false;
        },
        error: (err) => {
          console.error('Error loading more products:', err);
          this.isLoadingMore = false;
          this.errorMessage = err.error?.message || 'Error loading more products';
          setTimeout(() => {
            this.errorMessage = '';
          }, 5000);
        }
      });
    } else {
      this.hasMoreData = false;
    }
  }

  loadCategories() {
    this.apiService.getCategories(1, 1000, '', 'Active').subscribe({
      next: (response: any) => {
        const data = (response && response.data !== undefined) ? response.data : response;
        if (data && data.categories) {
          this.categories = data.categories.filter((cat: any) => cat.isActive);
        } else if (Array.isArray(data)) {
          this.categories = data.filter((cat: any) => cat.isActive);
        } else {
          this.categories = [];
        }
      },
      error: (err) => {
        console.error('Error loading categories:', err);
        this.categories = [];
      }
    });
  }

  onSearch() {
    // Reload products with new search term (pagination will reset)
    this.loadProducts();
  }

  applyFilters() {
    // Reload products with new filters (pagination will reset)
    this.loadProducts();
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCategory = '';
    this.selectedStatus = '';
    this.loadProducts();
  }

  toggleActionMenu(productId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    Object.keys(this.showActionMenu).forEach(key => {
      if (key !== productId) {
        this.showActionMenu[key] = false;
      }
    });
    this.showActionMenu[productId] = !this.showActionMenu[productId];
  }

  closeActionMenu() {
    Object.keys(this.showActionMenu).forEach(key => {
      this.showActionMenu[key] = false;
    });
  }

  getCategoryName(categoryId: string): string {
    const category = this.categories.find(c => c._id === categoryId);
    return category?.name || 'N/A';
  }

  /** Category id for navigating to category page with view modal (Admin/Manager) */
  getCategoryIdForView(product: any): string | null {
    if (!product?.category) return null;
    const c = product.category;
    return c._id || (typeof c === 'string' ? c : null) || null;
  }

  getProductId(product: any): string {
    // Use productId from database if available, otherwise fallback to generated ID
    if (product.productId) {
      return product.productId;
    }
    // Fallback: generate from _id if productId doesn't exist
    if (product._id) {
      const idStr = product._id.toString();
      const lastPart = idStr.slice(-4);
      return `P${lastPart.padStart(4, '0')}`;
    }
    return 'P0000';
  }

  getSharedCount(product: any): string {
    const count = product.sharedSuppliers?.length || product.sharedCount || 0;
    return String(count).padStart(2, '0');
  }

  isSharedCountZero(product: any): boolean {
    const count = product.sharedSuppliers?.length || product.sharedCount || 0;
    return count === 0;
  }

  formatDate(date: any): string {
    if (!date) return '-';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  viewProductDetails(product: any) {
    // Navigate to product view/details page
    this.router.navigate(['/product/view', product._id]);
    this.closeActionMenu();
  }

  editProduct(product: any) {
    // Navigate to product management page with product ID
    // Navigate to add product page for editing
    window.location.href = `/product/add?id=${product._id}`;
    this.closeActionMenu();
  }

  toggleProductStatus(product: any) {
    this.productToToggle = product;
    const action = product.isActive ? 'deactivate' : 'activate';
    this.modalMessage = `Do you want to ${action} this product ${product.name}?`;
    this.modalConfirmText = action === 'deactivate' ? 'Deactivate' : 'Activate';
    this.showConfirmModal = true;
    this.closeActionMenu();
  }

  onConfirmStatusChange() {
    if (!this.productToToggle) return;

    const newStatus = !this.productToToggle.isActive;
    this.apiService.updateProductStatus(this.productToToggle._id, newStatus).subscribe({
      next: () => {
        // Update the product in the current list
        const index = this.products.findIndex(p => p._id === this.productToToggle._id);
        if (index !== -1) {
          this.products[index].isActive = newStatus;
        }
        this.closeConfirmModal();
      },
      error: (err) => {
        this.showErrorNotification(err.error?.message || 'Error updating product status');
        this.closeConfirmModal();
      }
    });
  }

  closeConfirmModal() {
    this.showConfirmModal = false;
    this.productToToggle = null;
    this.modalMessage = '';
    this.modalConfirmText = 'Confirm';
  }

  deleteProduct(id: string) {
    if (confirm('Are you sure you want to delete this product?')) {
      this.apiService.deleteProduct(id).subscribe({
        next: () => {
          // Remove product from current list
          this.products = this.products.filter(p => p._id !== id);
          this.totalItems = Math.max(0, this.totalItems - 1);
          this.closeActionMenu();
          this.loadProducts();
        },
        error: (err) => {
          this.showErrorNotification(err.error?.message || 'Error deleting product');
        }
      });
    }
  }

  showErrorNotification(message: string) {
    this.errorMessage = message;
    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      this.errorMessage = '';
    }, 5000);
  }

  getProductImageUrl(product: any): string {
    if (!product) return '';
    
    // Use image field if available (new field - priority)
    if (product.image) {
      // If already a full URL, return as is
      if (product.image.startsWith('http://') || product.image.startsWith('https://')) {
        return product.image;
      }
      // Otherwise construct S3 URL (if needed)
      return product.image;
    }
    
    // Fallback: Check if product has image in specifications (legacy)
    if (product.specifications && Array.isArray(product.specifications)) {
      const imageSpec = product.specifications.find((spec: any) => 
        spec.keyType === 'Upload' && spec.value
      );
      if (imageSpec && imageSpec.value) {
        return imageSpec.value;
      }
    }
    
    // Fallback: Check if category has image
    if (product.category?.image) {
      return product.category.image;
    }
    
    return '';
  }

  getProductImage(product: any): string {
    // Use the new method
    return this.getProductImageUrl(product);
  }

  onProductImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
      const placeholder = img.nextElementSibling as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    }
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
      const placeholder = img.nextElementSibling as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    }
  }
}
